import React from 'react';
import './scheduled-zoom-meeting-details.scss';
import * as IoC from 'typescript-ioc';
import { FormattedMessage, injectIntl, IntlShape } from 'react-intl';
import { DateTimeFormatter } from '../../../../../infrastructure/implementations/date-formatter';
import { connectZoomVM } from '../../../zoom.context';
import { Link } from '../../../../../infrastructure/components/router-extensions';
import DefaultButton from '../../../../global/default-button/default-button';
import { Routes } from '../../../../../routes-table';
import { DetailsItemText } from '../../../../global/_details/_details-item/_details-item-text/details-item-text.presentation';
import { DetailsItemLink } from '../../../../global/_details/_details-item/_details-item-link/details-item-link';
import { DetailsItemDateTime } from '../../../../global/_details/_details-item/_details-item-date-time/details-item-date-time';
import { ResourcesNavigation } from '../../../../_events/_manage-event/shared/navigation/navigation';
import { ZoomMeetingWithDateFields } from './scheduled-zoom-meeting-details.viewmodel';
import { DeleteZoomMeetingButton } from './_delete-zoom-meeting-button/delete-zoom-meeting-button';
import { isZoomMeetingReccuring } from '../../../../../infrastructure/utils/connections.utils';
import { LOADING, TOPIC_TEXT } from '../../../../../constants/common-labels.constants';

export interface IScheduledZoomMeetingDetailsPresentationProps {
    deleteHandler: () => void;
    meetingDetails: ZoomMeetingWithDateFields;
    loading: boolean;
    error: string | null;
    intl?: IntlShape;
}

const dateTimeFormatter = IoC.Container.get<DateTimeFormatter>(DateTimeFormatter);

const ScheduledZoomMeetingDetailsPresentation = (props: IScheduledZoomMeetingDetailsPresentationProps) => {
    const zoomMeetingReccuring: boolean = isZoomMeetingReccuring(props.meetingDetails);

    return (
        <div>
            <section className='resources-tabs horizontal-row scheduled-meetings-list__horizontal-row'>
                <ResourcesNavigation />
            </section>
            <div className='page-container'>
                <div className='zoom-meeting-details'>
                    <h1>
                        <FormattedMessage
                            id='oe.zoom.meetingDetails'
                            defaultMessage='Zoom Meeting Details'
                            description='Label text'
                        />
                    </h1>
                    {props.loading ?
                        <span>
                            <FormattedMessage
                                {...LOADING}
                                values={{
                                    suffix: '...'
                                }}
                            />
                        </span> :
                        <>
                            <div className='buttons-block controll-btns'>
                                {!zoomMeetingReccuring
                                    ?
                                        <>
                                            <Link routeEnum={Routes.ZoomUpdateMeeting}
                                                  routeParams={{meetingId: props.meetingDetails.id.toString()}}>
                                                <DefaultButton modifier='slim' text={
                                                    <FormattedMessage
                                                        id='oe.common.update'
                                                        defaultMessage='Update'
                                                        description='Label text'
                                                    />
                                                }/>
                                            </Link>
                                            <Link routeEnum={Routes.ZoomScheduleNewMeeting}
                                                  routeParams={{clonedMeetingId: props.meetingDetails.id.toString()}}>
                                                <DefaultButton modifier='slim' text={
                                                    <FormattedMessage
                                                        id='oe.common.clone'
                                                        defaultMessage='Clone'
                                                        description='Label text'
                                                    />
                                                }/>
                                            </Link>
                                        </>
                                    :
                                    null
                                }
                                <DeleteZoomMeetingButton deleteHandler={props.deleteHandler}/>
                            </div>
                            {!zoomMeetingReccuring
                                ?
                                    <>
                                    <DetailsItemDateTime
                                        title={
                                            <FormattedMessage
                                                id='oe.datetime.start'
                                                defaultMessage='Start Datetime'
                                                description='Label text'
                                            />
                                        }
                                        time={dateTimeFormatter.dateToLocalString(props.meetingDetails.startTime)}
                                        timeInDifferentTZ={dateTimeFormatter.dateToLocalString(props.meetingDetails.startTime, props.meetingDetails.timeZone)}
                                        timeZone={props.meetingDetails.timeZone}
                                    />
                                    <DetailsItemDateTime
                                        title={
                                            <FormattedMessage
                                                id='oe.datetime.end'
                                                defaultMessage='End Datetime'
                                                description='Label text'
                                            />
                                        }
                                        time={dateTimeFormatter.dateToLocalString(props.meetingDetails.endTime)}
                                        timeInDifferentTZ={dateTimeFormatter.dateToLocalString(props.meetingDetails.endTime, props.meetingDetails.timeZone)}
                                        timeZone={props.meetingDetails.timeZone}
                                    />
                                    </>
                                :
                                null
                            }
                            <DetailsItemText
                                title={
                                    <FormattedMessage
                                        id='oe.host.email'
                                        defaultMessage='Host Email'
                                        description='Label text'
                                    />
                                }
                                value={props.meetingDetails.zoomUser.email}
                            />
                            <DetailsItemText
                                title={
                                    <FormattedMessage
                                        id='oe.host.firstName'
                                        defaultMessage='Host First Name'
                                        description='Label text'
                                    />
                                }
                                value={props.meetingDetails.zoomUser.firstName}
                            />
                            <DetailsItemText
                                title={
                                    <FormattedMessage
                                        id='oe.host.lastName'
                                        defaultMessage='Host Last Name'
                                        description='Label text'
                                    />
                                }
                                value={props.meetingDetails.zoomUser.lastName}
                            />
                            {props.meetingDetails.zoomUser.groups && props.meetingDetails.zoomUser.groups.length ?
                                <DetailsItemText
                                    title={
                                        <FormattedMessage
                                            id='oe.host.groups'
                                            defaultMessage='Host Groups'
                                            description='Label text'
                                        />
                                    }
                                    value={props.meetingDetails.zoomUser.groups.map(g => g.name).join(', ')}
                                /> :
                                <DetailsItemText
                                    title={
                                        <FormattedMessage
                                        id='oe.host.groups'
                                        defaultMessage='Host Groups'
                                        description='Label text'
                                    />}
                                    value={
                                        props.intl.formatMessage({
                                            id: `oe.common.none`,
                                            defaultMessage: '{prefix}None{suffix}',
                                            description: 'Label text',
                                        }, {
                                            prefix: '(',
                                            suffix: ')'
                                        })}
                                />}
                            {props.meetingDetails.topic &&
                            <DetailsItemText
                                title={<FormattedMessage {...TOPIC_TEXT} />}
                                value={props.meetingDetails.topic}
                            />}
                            <DetailsItemText
                                title={
                                    <FormattedMessage
                                        id='oe.meeting.id'
                                        defaultMessage='Meeting ID'
                                        description='Label text'
                                    />
                                }
                                value={props.meetingDetails.zoomId}
                            />
                            {props.meetingDetails.password &&
                            <DetailsItemText
                                title={
                                    <FormattedMessage
                                        id='oe.meeting.password'
                                        defaultMessage='Meeting Password'
                                        description='Label text'
                                    />
                                }
                                value={props.meetingDetails.password}
                                copyButton={true}
                            />}
                            <DetailsItemLink
                                title={
                                    <FormattedMessage
                                        id='oe.link.invite'
                                        defaultMessage='Invite Link'
                                        description='Label text'
                                    />
                                }
                                value={props.meetingDetails.inviteLink}
                            />
                            {props.meetingDetails.inviteLinkWithPassword ?
                                <DetailsItemLink
                                    title={
                                        <FormattedMessage
                                            id='oe.inviteLink.withEmbeddedPassword'
                                            defaultMessage='Invite Link with Embedded Password'
                                            description='Label text'
                                        />
                                    }
                                    value={props.meetingDetails.inviteLinkWithPassword}
                                /> :
                                <DetailsItemText
                                    title={
                                        <FormattedMessage
                                            id='oe.inviteLink.withEmbeddedPassword'
                                            defaultMessage='Invite Link with Embedded Password'
                                            description='Label text'
                                        />
                                    }
                                    value={'(Not available)'}
                                />}
                            <DetailsItemLink
                                title={
                                    <FormattedMessage
                                    id='oe.host.url'
                                    defaultMessage='Host URL'
                                    description='Label text'
                                />
                                }
                                value={props.meetingDetails.startUrl}
                            />
                            <DetailsItemText
                                title={
                                    <FormattedMessage
                                        id='oe.common.status'
                                        defaultMessage='Status'
                                        description='Label text'
                                    />
                                }
                                value={props.meetingDetails.status}
                            />
                            {props.meetingDetails.agenda &&
                            <DetailsItemText
                                title={
                                    <FormattedMessage
                                        id='oe.common.agenda'
                                        defaultMessage='Agenda'
                                        description='Label text'
                                    />
                                }
                                value={props.meetingDetails.agenda}/>
                            }
                            {props.meetingDetails.invitation &&
                            <DetailsItemText
                                title={
                                    <FormattedMessage
                                        id='oe.meeting.invitation'
                                        defaultMessage='Meeting Invitation'
                                        description='Label text'
                                    />
                                }
                                value={props.meetingDetails.invitation}
                                className='invitation'
                                copyButton
                            />}
                        </>
                    }
                </div>
            </div>
        </div>
    );
};

const scheduledZoomMeetingDetailsMapperFunction = zoomVM => {
    const vm = zoomVM.getScheduledMeetingDetailsVM();
    const meetingDetails = vm.getCurrentMeetingDetails();
    const loading = vm.isLoading();
    const error = vm.getError();
    return {
        deleteHandler: vm.deleteCurrentMeeting,
        meetingDetails: meetingDetails,
        loading: loading,
        error: error
    };
};

const ScheduledZoomMeetingDetails = injectIntl(
    connectZoomVM<IScheduledZoomMeetingDetailsPresentationProps>(
        ScheduledZoomMeetingDetailsPresentation,
        scheduledZoomMeetingDetailsMapperFunction
    )
);

export { ScheduledZoomMeetingDetails, scheduledZoomMeetingDetailsMapperFunction, ScheduledZoomMeetingDetailsPresentation };
